import { useEffect, useState } from "react";
import { Users, Wallet as WalletIcon, Receipt, Shield, CheckCircle2, XCircle, EyeOff, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SeoTab } from "@/components/admin/SeoTab";
import { useAuth } from "@/context/AuthContext";
import { AdminInsights } from "@/components/AdminInsights";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [listings, setListings] = useState([]);
  const [audit, setAudit] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [ops, setOps] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState({});
  const [llmStatus, setLlmStatus] = useState(null);

  const load = async () => {
    try {
      const [o, r, ord, u, p, l, a, sm, an, rs, ops] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/wallet-requests?status=pending"),
        api.get("/orders/admin/all?status=pending"),
        api.get("/admin/users"),
        api.get("/admin/payments"),
        api.get("/admin/listings"),
        api.get("/admin/audit"),
        api.get("/admin/stripe-mode"),
        api.get("/admin/analytics/revenue"),
        api.get("/admin/revenue-summary"),
        api.get("/admin/ops/summary"),
      ]);
      setOverview(o.data); setRequests(r.data); setOrders(ord.data); setUsers(u.data);
      setPayments(p.data); setListings(l.data); setAudit(a.data); setStripeMode(sm.data);
      setAnalytics(an.data);
      setRevenueSummary(rs.data);
      setOps(ops.data);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
    }
  };
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => {
    if (user) api.get("/admin/llm-status").then(({ data }) => setLlmStatus(data)).catch(() => {});
  }, [user]);

  const action = async (id, kind, reason) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const url = `/admin/wallet-requests/${id}/${kind}`;
      const body = kind === "reject" ? { reason: reason || "Not approved" } : {};
      await api.post(url, body);
      toast.success(`Request ${kind}d`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const hideListing = async (id) => {
    try { await api.post(`/admin/listings/${id}/hide`); toast.success("Listing hidden"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const confirmOrder = async (id, note) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/orders/admin/${id}/confirm`, { note: note || "" });
      toast.success("Order confirmed"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const rejectOrder = async (id, reason) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/orders/admin/${id}/reject`, { note: reason || "Could not confirm payment" });
      toast.success("Order rejected"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 font-display text-2xl">Admin only</h1>
        <p className="mt-2 text-sm text-muted-foreground">This page is restricted to Zuntria staff.</p>
      </div>
    );
  }
  if (!user) return <div className="p-8 text-muted-foreground">Please log in.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Admin dashboard</h1>

      {llmStatus && llmStatus.status !== "ok" && (
        <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm" data-testid="llm-status-banner">
          <span className="mr-2 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-400">
            AI {llmStatus.status === "budget_exceeded" ? "budget empty" : "degraded"}
          </span>
          <span className="text-muted-foreground">
            {llmStatus.status === "budget_exceeded"
              ? "Your Emergent Universal Key balance is empty — assistants, Truffl outreach and Universal Ask are running on fallback templates. Top up: Profile → Universal Key → Add Balance."
              : `AI health check failed: ${llmStatus.detail || "unknown error"}`}
          </span>
        </div>
      )}

      {stripeMode && (
        <div
          className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm"
          data-testid="payments-status-banner"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-400">
            PayPal: Live
          </span>
          <span className="text-muted-foreground">
            Fully automated checkout & AI delivery — orders fulfil instantly with zero manual action. You'll get a notification for every sale.
          </span>
        </div>
      )}

      {overview && (
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-overview">
          <Stat label="Users" value={overview.users} icon={Users} />
          <Stat label="Active listings" value={overview.active_listings} />
          <Stat label="Pending orders" value={orders.length} />
          <Stat
            label="Total revenue"
            value={`£${Number(revenueSummary?.total_revenue ?? overview.total_revenue ?? 0).toFixed(2)}`}
            icon={Receipt}
          />
          <Stat
            label="Platform revenue (fees)"
            value={`£${Number(overview.platform_revenue ?? 0).toFixed(2)}`}
            icon={Receipt}
          />
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="flex w-full max-w-full flex-nowrap justify-start overflow-x-auto">
          <TabsTrigger value="insights" data-testid="tab-insights">Analytics</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Revenue</TabsTrigger>
          <TabsTrigger value="ops" data-testid="tab-ops">Ops</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="blast" data-testid="tab-blast">Re-engage</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="mt-6">
          <ControlPlanePanel />
          <ReconciliationPanel />
          <AdminInsights />
        </TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 ? <Empty msg="No pending orders — autopilot delivers instantly" /> : orders.map((o) => (
            <OrderRow key={o.order_id} o={o} busy={busy[o.order_id]} confirm={confirmOrder} reject={rejectOrder} />
          ))}
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">All Stripe payment transactions. Use CSV export to reconcile.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadCsv("/admin/payments.csv", "zuntria-payments.csv")} data-testid="export-payments-csv">
                <Download className="mr-1 h-3 w-3" /> Payments CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadCsv("/admin/orders.csv", "zuntria-orders.csv")} data-testid="export-orders-csv">
                <Download className="mr-1 h-3 w-3" /> Orders CSV
              </Button>
            </div>
          </div>
          <Table headers={["When", "User", "Kind", "Package", "Amount", "Status"]}>
            {payments.slice(0, 50).map((p) => (
              <tr key={p.session_id} className="border-t border-border" data-testid={`pay-${p.session_id?.slice(0, 8)}`}>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{p.user_email}</td>
                <td className="px-3 py-2 text-xs capitalize">{p.kind}</td>
                <td className="px-3 py-2 text-xs">{p.label || p.package_id}</td>
                <td className="px-3 py-2 text-xs">£{Number(p.amount).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${p.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-400" : "bg-secondary"}`}>{p.payment_status}</span>
                </td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <Table headers={["When", "Name", "Email", "Wallet", "Plan", "Trust"]}>
            {users.slice(0, 50).map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-testid={`user-${u.user_id?.slice(0, 8)}`}>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-xs">{u.name}</td>
                <td className="px-3 py-2 text-xs">{u.email}</td>
                <td className="px-3 py-2 text-xs">£{Number(u.wallet || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs capitalize">{u.subscription_plan || "free"}</td>
                <td className="px-3 py-2 text-xs">{u.trust_score ?? 50}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="listings" className="mt-6">
          <SellerVerifications />
          <Table headers={["When", "Title", "Seller", "Price", "Status", ""]}>
            {listings.slice(0, 50).map((l) => (
              <tr key={l.listing_id} className="border-t border-border" data-testid={`listing-${l.listing_id?.slice(0, 8)}`}>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-xs">{l.title}</td>
                <td className="px-3 py-2 text-xs">{l.seller_name}</td>
                <td className="px-3 py-2 text-xs">£{Number(l.price).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs capitalize">{l.status}</td>
                <td className="px-3 py-2 text-xs">
                  {l.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => hideListing(l.listing_id)} data-testid={`hide-${l.listing_id}`}>
                      <EyeOff className="mr-1 h-3 w-3" /> Hide
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <Table headers={["When", "Admin", "Action", "Target", "Meta"]}>
            {audit.slice(0, 100).map((a, i) => (
              <tr key={i} className="border-t border-border" data-testid={`audit-${i}`}>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{a.admin_email}</td>
                <td className="px-3 py-2 text-xs font-mono">{a.action}</td>
                <td className="px-3 py-2 text-xs font-mono">{a.target}</td>
                <td className="px-3 py-2 text-[10px] text-muted-foreground">{JSON.stringify(a.meta)}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6 space-y-6" data-testid="analytics-tab">
          <CleanupTestData />
          {revenueSummary && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="revenue-summary">
              <Stat label="Total revenue" value={`£${Number(revenueSummary.total_revenue).toFixed(2)}`} icon={Receipt} />
              <Stat label="This month" value={`£${Number(revenueSummary.revenue_this_month).toFixed(2)}`} />
              <Stat label="Last 30 days" value={`£${Number(revenueSummary.revenue_last_30d).toFixed(2)}`} />
              <Stat label="MRR" value={`£${Number(revenueSummary.mrr).toFixed(2)}`} />
              <Stat label="ARPU" value={`£${Number(revenueSummary.arpu).toFixed(2)}`} />
              <Stat label="Paying users" value={revenueSummary.paying_users} />
              <Stat label="Pro subs" value={revenueSummary.pro_subs} />
              <Stat label="Business subs" value={revenueSummary.business_subs} />
            </div>
          )}
          {analytics && (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat label="Total revenue" value={`£${Number(analytics.total_revenue).toFixed(2)}`} icon={Receipt} />
                <Stat label="Last 30 days" value={`£${Number(analytics.last_30_days).toFixed(2)}`} />
                <Stat label="Last 7 days" value={`£${Number(analytics.last_7_days).toFixed(2)}`} />
                <Stat label="MRR (est.)" value={`£${Number(analytics.mrr_estimate).toFixed(2)}`} />
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat label="ARR (est.)" value={`£${Number(analytics.arr_estimate).toFixed(2)}`} />
                <Stat label="Premium users" value={analytics.active_premium_users} />
              </div>
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-display text-lg">Top products by revenue</h3>
                {analytics.top_products?.length ? (
                  <div className="mt-4 space-y-2">
                    {analytics.top_products.map((p) => (
                      <div key={p.product_id} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`top-${p.product_id}`}>
                        <span className="capitalize">{p.product_id.replace(/_/g, ' ')}</span>
                        <span className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{p.sales} sales</span>
                          <span className="font-display text-base text-foreground">£{Number(p.revenue).toFixed(2)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">No paid orders yet — share your Pricing URL to start earning.</p>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="blast" className="mt-6">
          <BlastPanel />
        </TabsContent>

        <TabsContent value="seo" className="mt-6" data-testid="seo-tab-content">
          <SeoTab />
        </TabsContent>

        <TabsContent value="payouts" className="mt-6" data-testid="payouts-tab">
          <PayoutsPanel />
        </TabsContent>

        <TabsContent value="ops" className="mt-6 space-y-4" data-testid="ops-tab">
          <SystemAlertsPanel />
          <SlackWebhookPanel />
          <WhatsAppPanel />
          <GoalGatePanel />
          <OnboardingFunnelPanel />
          <IntegrationMetricsPanel />
          <PartnersAdminPanel />
          {ops && (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat label="Signups today" value={ops.signups.today} />
                <Stat label="Signups 7d" value={ops.signups.last_7d} />
                <Stat label="Signups 30d" value={ops.signups.last_30d} />
                <Stat label="Paywall hits 7d" value={ops.paywall_hits.last_7d} />
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat label="CV download attempts" value={ops.cv_download_attempts} />
                <Stat label="Pro conversions (total)" value={ops.pro_conversions.total} />
                <Stat label="Pro conversions 7d" value={ops.pro_conversions.last_7d} />
                <Stat label="Paywall hits total" value={ops.paywall_hits.total} />
              </div>
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-display text-lg">Roadmap dropoff (active missions)</h3>
                <div className="mt-4 grid grid-cols-7 gap-2">
                  {ops.mission_dropoff.map((d) => (
                    <div key={d.day} className="rounded-lg border border-border bg-background p-3 text-center" data-testid={`dropoff-${d.day}`}>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Day {d.day}</div>
                      <div className="mt-1 font-display text-2xl">{d.users_completed}</div>
                      <div className="text-[10px] text-muted-foreground">completed</div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">Tip: Day 3+ is gated behind Pro for free users — high Day-2 drop indicates the gate is converting attention into paywall hits.</p>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GoalGatePanel() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/goal-gate").then(({ data }) => setEnabled(!!data.enabled)).catch(() => {});
  }, []);
  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/goal-gate", { enabled: !enabled });
      setEnabled(!!data.enabled);
      toast.success(`Mandatory Goal Selector ${data.enabled ? "ON" : "OFF"}`);
    } catch { toast.error("Could not update setting"); }
    finally { setSaving(false); }
  };
  if (enabled === null) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-5" data-testid="goal-gate-panel">
      <div>
        <h3 className="font-display text-lg">Mandatory Goal Selector</h3>
        <p className="text-xs text-muted-foreground">Blocks the dashboard for users without a goal until they pick one (Get my first client / Find a provider / Manage my rentals).</p>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        data-testid="goal-gate-toggle"
        className={`relative h-7 w-12 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-border"}`}
      >
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function BlastPanel() {
  const TEMPLATES = {
    founding: {
      label: "Founding Pro offer",
      subject: "The first 10 Founding Lifetime Pro spots are live",
      body: `<h2>The first 10 Founding Lifetime Pro spots are live</h2>
<p>Hi {{name}},</p>
<p>You signed up to Zuntria but haven't tried Pro yet. We're opening 10 founding spots — pay <strong>£14 once</strong> and get Pro forever.</p>
<p>That's the last £14 you ever pay Zuntria — every other user pays £14.99/month.</p>
<p><a href="https://zuntria.co.uk/pricing?founding=1" style="display:inline-block;background:#f97316;color:#fff;padding:12px 22px;border-radius:8px;text-decoration:none;font-weight:600;">Claim my Founding spot →</a></p>
<p style="color:#9ca3af;">Only 10 spots. Once they're gone, this offer never comes back.</p>`,
    },
    languages: {
      label: "Language spotlight 🌍",
      subject: "Zuntria now speaks Yorùbá, Igbo, Hausa, Swahili, Filipino & Patois 🌍",
      body: `<h2>Zuntria now speaks your language 🌍</h2>
<p>Hi {{name}},</p>
<p>Big update: Zuntria is now fully multilingual. Tap the globe icon and the whole app — <strong>and every AI assistant</strong> — switches to your language:</p>
<p>🇳🇬 Yorùbá · Igbo · Hausa &nbsp;|&nbsp; 🇰🇪 Kiswahili &nbsp;|&nbsp; 🇵🇭 Filipino &nbsp;|&nbsp; 🇯🇲 Jamaican Patois &nbsp;|&nbsp; 🇬🇧 English</p>
<p>Prices now show in your local currency too — ₦, KSh, ₱, J$ and more.</p>
<p>And there's something brand new waiting for you: <strong>Truffl</strong> — our client discovery engine that finds local businesses near you, scores the best opportunities, and writes your outreach messages with AI. Your first 3 searches are free.</p>
<p><a href="https://zuntria.co.uk/truffl" style="display:inline-block;background:#f97316;color:#fff;padding:12px 22px;border-radius:8px;text-decoration:none;font-weight:600;">Find my first client →</a></p>
<p style="color:#9ca3af;">Share this with someone who hustles in their mother tongue. 💛</p>`,
    },
  };
  const defaultBody = TEMPLATES.founding.body;
  const [subject, setSubject] = useState("The first 10 Founding Lifetime Pro spots are live");
  const [bodyHtml, setBodyHtml] = useState(defaultBody);
  const [cohort, setCohort] = useState("free");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const dryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blast", { subject, body_html: bodyHtml, cohort, dry_run: true });
      setLast(data);
      toast.success(`${data.queued} ${cohort} users would receive this email`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dry-run failed");
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!window.confirm(`Send to ALL ${cohort} users? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blast", { subject, body_html: bodyHtml, cohort, dry_run: false });
      setLast(data);
      toast.success(`Sent ${data.sent}/${data.queued} · failed: ${data.failed}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blast failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="blast-panel">
      <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-4 text-sm">
        <strong className="text-orange-400">Re-engage panel.</strong>{" "}
        Send a one-shot email to all <strong>free</strong> or <strong>premium</strong> users. Uses your Resend integration.
        Hard cap: 500 recipients per blast · 12h dedupe on subject+cohort.
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[auto_1fr_auto]">
        <select
          onChange={(e) => {
            const tpl = TEMPLATES[e.target.value];
            if (tpl) { setSubject(tpl.subject); setBodyHtml(tpl.body); }
          }}
          defaultValue="founding"
          className="h-10 rounded-md border border-border bg-background px-3 text-sm"
          data-testid="blast-template"
        >
          {Object.entries(TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" data-testid="blast-subject" />
        <select
          value={cohort}
          onChange={(e) => setCohort(e.target.value)}
          className="h-10 rounded-md border border-border bg-background px-3 text-sm"
          data-testid="blast-cohort"
        >
          <option value="free">Free users</option>
          <option value="premium">Pro / Lifetime / Business</option>
          <option value="all">All users</option>
        </select>
      </div>

      <textarea
        value={bodyHtml}
        onChange={(e) => setBodyHtml(e.target.value)}
        rows={12}
        className="w-full rounded-md border border-border bg-background p-3 font-mono text-xs"
        placeholder="Body HTML"
        data-testid="blast-body"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={dryRun} disabled={busy} variant="outline" data-testid="blast-dry-run">
          {busy ? "Checking..." : "Dry-run (count recipients)"}
        </Button>
        <Button onClick={send} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="blast-send">
          {busy ? "Sending..." : "Send to all"}
        </Button>
        {last && (
          <span className="text-xs text-muted-foreground" data-testid="blast-last">
            Last: queued {last.queued} · sent {last.sent} · failed {last.failed}{last.dry_run ? " (dry-run)" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-2 flex items-center gap-2 font-display text-2xl">
        {Icon && <Icon className="h-5 w-5 text-primary" />} {value}
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">{msg}</div>;
}

function PayoutsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("requested");
  const [busy, setBusy] = useState({});

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/payouts${q}`).then((r) => setRows(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (id, kind, note) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const body = kind === "send" ? {} : { note: note || (kind === "reject" ? "Rejected by admin" : "Paid manually") };
      await api.post(`/admin/payouts/${id}/${kind}`, body);
      toast.success(kind === "send" ? "Payout sent via PayPal" : kind === "mark-paid" ? "Marked as paid" : "Rejected & refunded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
        <strong className="text-emerald-400">PayPal Payouts.</strong>{" "}
        Auto-mode: <strong>{rows[0]?.note?.includes("auto") ? "on" : "manual review"}</strong>. Set
        <code className="mx-1 rounded bg-background px-1 py-0.5">PAYPAL_PAYOUTS_AUTO=true</code>
        in backend .env to enable instant API transmission (requires PayPal Payouts enabled on your business account).
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {["requested", "sent", "failed", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full border px-3 py-1 text-xs capitalize ${filter === s ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"}`} data-testid={`payouts-filter-${s}`}>
            {s}
          </button>
        ))}
      </div>

      {rows.length === 0 ? <Empty msg="No payouts in this view yet." /> : (
        <Table headers={["When", "User", "PayPal email", "Amount", "Status", "Actions"]}>
          {rows.map((p) => (
            <tr key={p.payout_id} className="border-b border-border last:border-0" data-testid={`payout-row-${p.payout_id}`}>
              <td className="py-2 pr-2 text-muted-foreground">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="py-2 pr-2">{p.user_name || p.user_email}</td>
              <td className="py-2 pr-2 text-xs">{p.paypal_email}</td>
              <td className="py-2 pr-2 font-semibold">£{Number(p.amount).toFixed(2)}</td>
              <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                p.status === "sent" ? "bg-emerald-500/20 text-emerald-400" :
                p.status === "rejected" ? "bg-red-500/20 text-red-400" :
                p.status === "failed" ? "bg-red-500/20 text-red-400" :
                "bg-amber-500/20 text-amber-400"
              }`}>{p.status}</span></td>
              <td className="py-2 pr-2">
                {(p.status === "requested" || p.status === "failed") && (
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" onClick={() => act(p.payout_id, "send")} disabled={busy[p.payout_id]} className="h-7 bg-emerald-500 px-2 text-xs text-white hover:bg-emerald-600" data-testid={`payout-send-${p.payout_id}`}>
                      Send via PayPal
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(p.payout_id, "mark-paid")} disabled={busy[p.payout_id]} className="h-7 px-2 text-xs" data-testid={`payout-markpaid-${p.payout_id}`}>
                      Mark paid (manual)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { const r = window.prompt("Reason?", "Insufficient verification"); if (r) act(p.payout_id, "reject", r); }} disabled={busy[p.payout_id]} className="h-7 border-red-500/40 px-2 text-xs text-red-400 hover:bg-red-500/10" data-testid={`payout-reject-${p.payout_id}`}>
                      Reject
                    </Button>
                  </div>
                )}
                {p.status === "sent" && p.batch_id && (
                  <span className="text-[10px] text-muted-foreground">Batch: {p.batch_id}</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full text-left">
        <thead className="bg-secondary/40">
          <tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function RequestRow({ r, busy, action }) {
  const [reason, setReason] = useState("");
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`req-row-${r.request_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{r.kind} · {new Date(r.created_at).toLocaleString()}</div>
          <div className="mt-1 font-display text-lg">£{Number(r.amount).toFixed(2)} — {r.user_name} <span className="text-sm text-muted-foreground">({r.user_email})</span></div>
          {r.bank && (
            <div className="mt-2 text-xs text-muted-foreground">
              Bank: {r.bank.holder_name} · {r.bank.sort_code} · {r.bank.account_number} {r.bank.bank_name ? `· ${r.bank.bank_name}` : ""}
            </div>
          )}
          {r.method && <div className="mt-2 text-xs text-muted-foreground">Method: {r.method}</div>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => action(r.request_id, "approve")} disabled={busy} data-testid={`approve-${r.request_id}`}>
            <CheckCircle2 className="mr-1 h-3 w-3 text-emerald-500" /> Approve
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection (optional)" className="h-9 max-w-xs text-xs" data-testid={`reason-${r.request_id}`} />
        <Button size="sm" variant="outline" onClick={() => action(r.request_id, "reject", reason)} disabled={busy} data-testid={`reject-${r.request_id}`}>
          <XCircle className="mr-1 h-3 w-3 text-rose-500" /> Reject &amp; refund
        </Button>
      </div>
    </div>
  );
}

function OrderRow({ o, busy, confirm, reject }) {
  const [reason, setReason] = useState("");
  const waMsg = encodeURIComponent(`Hi ${o.user_name}, this is Zuntria. Just checking on your ${o.label} order (£${Number(o.amount).toFixed(2)}). Have you sent payment via ${o.method}? Your ref: ${o.payment_reference || "(none yet)"}.`);
  // Use the email's local part as a fallback contact prompt
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`order-row-${o.order_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Order · {new Date(o.created_at).toLocaleString()}</div>
          <div className="mt-1 font-display text-lg">{o.label} · £{Number(o.amount).toFixed(2)}</div>
          <div className="mt-1 text-sm text-muted-foreground">{o.user_name} ({o.user_email}) · paid via <span className="capitalize">{o.method?.replace("_", " ")}</span></div>
          {o.payment_reference && <div className="mt-2 text-xs"><span className="text-muted-foreground">Ref:</span> <span className="font-mono">{o.payment_reference}</span></div>}
          {o.note && <div className="mt-1 text-xs text-muted-foreground">Note: {o.note}</div>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button size="sm" onClick={() => confirm(o.order_id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`confirm-${o.order_id}`}>
            <CheckCircle2 className="mr-1 h-3 w-3" /> Confirm &amp; deliver
          </Button>
          <a href={`mailto:${o.user_email}?subject=${encodeURIComponent(`Zuntria — your ${o.label} order`)}&body=${waMsg}`} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid={`email-${o.order_id}`}>📧 Email user</a>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason if rejecting (optional)" className="h-9 max-w-xs text-xs" data-testid={`order-reason-${o.order_id}`} />
        <Button size="sm" variant="outline" onClick={() => reject(o.order_id, reason)} disabled={busy} data-testid={`order-reject-${o.order_id}`}>
          <XCircle className="mr-1 h-3 w-3 text-rose-500" /> Reject
        </Button>
      </div>
    </div>
  );
}


function SellerVerifications() {
  const [pending, setPending] = useState([]);
  const load = () => api.get("/marketplace/admin/seller-verifications").then((r) => setPending(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (uid, decision) => {
    try {
      await api.post(`/marketplace/admin/seller-verifications/${uid}/decide`, { decision });
      toast.success(decision === "verified" ? "Seller verified ✅" : "Seller restricted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  if (!pending.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="seller-verifications">
      <h3 className="font-display text-lg">🪪 Seller verifications pending ({pending.length})</h3>
      <ul className="mt-3 divide-y divide-border">
        {pending.map((p) => (
          <li key={p.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`sv-${p.user_id}`}>
            <span className="min-w-0">
              <strong>{p.full_name}</strong> · {p.email}
              <span className="block text-xs text-muted-foreground">{p.address} · DOB {p.date_of_birth}{p.business_name ? ` · ${p.business_name}` : ""}</span>
            </span>
            <span className="flex gap-2">
              <Button size="sm" onClick={() => decide(p.user_id, "verified")} data-testid={`sv-approve-${p.user_id}`}>Approve</Button>
              <Button size="sm" variant="outline" onClick={() => decide(p.user_id, "restricted")} data-testid={`sv-reject-${p.user_id}`}>Reject</Button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CleanupTestData() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Purge all test-account revenue entries and hide their listings? (Archived, reversible)")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/analytics/cleanup-test-data");
      setResult(data);
      toast.success(`Purged £${data.revenue_amount_purged} of test revenue — real revenue: £${data.real_platform_revenue_remaining}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="cleanup-test-data-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium">🧹 Clean test data</div>
          <p className="text-xs text-muted-foreground">Removes revenue entries + listings from automated test accounts so numbers reflect real income only. Safe to run anytime (idempotent, archived).</p>
        </div>
        <Button onClick={run} disabled={busy} variant="outline" data-testid="cleanup-test-data-btn">{busy ? "Cleaning…" : "Run cleanup"}</Button>
      </div>
      {result && (
        <p className="mt-2 text-xs text-emerald-500" data-testid="cleanup-result">
          {result.revenue_entries_purged} entries purged (£{result.revenue_amount_purged}) · {result.test_listings_hidden} test listings hidden · real platform revenue now £{result.real_platform_revenue_remaining}
        </p>
      )}
    </div>
  );
}

function WhatsAppPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/admin/test", { to: to.trim() });
      toast.success(data.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#25D366]/40 bg-[#25D366]/5 p-4" data-testid="whatsapp-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium">📲 WhatsApp integration (Twilio)</div>
          <p className="text-xs text-muted-foreground">
            Status: {status ? (status.configured ? <strong className="text-emerald-500">CONNECTED ✅</strong> : <strong className="text-amber-500">NOT CONFIGURED</strong>) : "…"}
            {status && !status.configured && " — add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM to backend .env (get them at twilio.com/console)."}
          </p>
        </div>
        <div className="flex gap-2">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+447700900123" className="h-9 w-40" data-testid="wa-test-number" />
          <Button size="sm" onClick={test} disabled={busy || !status?.configured} data-testid="wa-test-send-btn">
            {busy ? "Sending…" : "Send test"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function IntegrationMetricsPanel() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/integrations/admin/metrics").then(({ data }) => setM(data)).catch(() => {});
  }, []);
  if (!m) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="integration-metrics-panel">
      <h3 className="font-display text-lg">Integration Gateway metrics</h3>
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Gmail connections" value={m.connections.gmail} />
        <Stat label="Calendly connections" value={m.connections.calendly} />
        <Stat label="Active webhooks" value={m.connections.webhooks_active} />
        <Stat label="Verified emails sent" value={m.connections.emails_sent_verified} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Webhook success rate" value={m.webhooks.success_rate === null ? "—" : `${m.webhooks.success_rate}%`} />
        <Stat label="Job board clicks 7d" value={m.jobs.job_board_clicks_7d} />
        <Stat label="Applications total" value={m.jobs.applications_total} />
        <Stat label="Interviews live" value={m.jobs.applications_by_status?.interview || 0} />
      </div>
    </div>
  );
}

function SlackWebhookPanel() {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/slack-webhook").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/slack-webhook", { url: url.trim() });
      const { data } = await api.get("/admin/slack-webhook");
      setStatus(data); setUrl("");
      toast.success("Slack webhook saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid webhook URL"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    try { await api.post("/admin/slack-webhook/test"); toast.success("Test message delivered to Slack ✅"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Slack test failed"); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="slack-webhook-panel">
      <h3 className="font-display text-lg">Slack alert channel</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Founder alerts also post to Slack once connected. Create an Incoming Webhook at api.slack.com/apps → Incoming Webhooks, then paste the URL.
        {status?.configured ? <span className="ml-1 text-emerald-400">Connected ({status.url_masked})</span> : <span className="ml-1 text-amber-400">Not connected</span>}
      </p>
      <div className="mt-3 flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" className="h-9" data-testid="slack-webhook-input" />
        <Button size="sm" onClick={save} disabled={busy || !url.trim()} data-testid="slack-webhook-save">Save</Button>
        {status?.configured ? <Button size="sm" variant="outline" onClick={test} data-testid="slack-webhook-test">Send test</Button> : null}
      </div>
    </div>
  );
}

function SystemAlertsPanel() {
  const [alerts, setAlerts] = useState(null);
  useEffect(() => {
    api.get("/admin/system-alerts").then(({ data }) => setAlerts(data.alerts || [])).catch(() => setAlerts([]));
  }, []);
  if (!alerts) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="system-alerts-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">🛡️ System exception alerts</h3>
        <span className="text-xs text-muted-foreground">Checked every 30 min · CRITICAL also sent via WhatsApp</span>
      </div>
      {alerts.length === 0 ? (
        <p className="mt-3 text-sm text-emerald-400" data-testid="system-alerts-healthy">All clear — no CRITICAL or WARNING signals detected.</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {alerts.map((a) => (
            <li key={a.alert_id} className="flex items-start gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs" data-testid={`system-alert-${a.alert_id}`}>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${a.level === "CRITICAL" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>{a.level}</span>
              <span className="flex-1">{a.message}</span>
              <span className="shrink-0 text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OnboardingFunnelPanel() {
  const [f, setF] = useState(null);
  useEffect(() => {
    api.get("/funnel/admin/summary").then(({ data }) => setF(data)).catch(() => {});
  }, []);
  if (!f) return null;
  const LABELS = { intro_viewed: "Saw intro", goal_selected: "Picked goal", target_viewed: "Reached target step", completed: "Completed", skipped: "Skipped" };
  const trend = (s) => {
    const cur = f.this_week[s] || 0, prev = f.last_week[s] || 0;
    if (!prev) return cur > 0 ? "new" : "—";
    const d = Math.round(((cur - prev) / prev) * 100);
    return `${d >= 0 ? "+" : ""}${d}%`;
  };
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="onboarding-funnel-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg">Onboarding funnel (7 days)</h3>
        <span className="text-xs text-muted-foreground">
          {f.signups_7d} signups · all-time completion {f.overall_completion_pct}%
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
        {f.steps.map((s) => (
          <div key={s} className="rounded-lg border border-border bg-background p-3" data-testid={`funnel-step-${s}`}>
            <p className="text-2xl font-semibold">{f.this_week[s] || 0}</p>
            <p className="text-xs text-muted-foreground">{LABELS[s]}</p>
            <p className={`mt-1 text-[11px] ${String(trend(s)).startsWith("+") ? "text-emerald-400" : "text-muted-foreground"}`}>
              vs last wk: {trend(s)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnersAdminPanel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", offer: "", url: "", category: "general", cta: "Get offer" });
  const load = () => api.get("/partners/admin/stats").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const toggle = async (p) => {
    await api.patch(`/partners/admin/${p.partner_id}`, { active: !p.active }).catch(() => {});
    load();
  };
  const saveUrl = async (p, url) => {
    if (!url || url === p.url) return;
    await api.patch(`/partners/admin/${p.partner_id}`, { url }).catch(() => {});
    load();
  };
  const add = async () => {
    if (!form.name || !form.offer || !form.url) return;
    await api.post("/partners/admin", form).catch(() => {});
    setForm({ name: "", offer: "", url: "", category: "general", cta: "Get offer" });
    load();
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="partners-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg">Partner catalogue & upsells</h3>
        <span className="text-xs text-muted-foreground">
          Milestone upsells: {data.milestone_upsells_sent} sent · {data.milestone_upsells_converted} → Pro
          {data.milestone_upsells_sent ? ` (${Math.round((data.milestone_upsells_converted / data.milestone_upsells_sent) * 100)}%)` : ""}
        </span>
      </div>
      {data.upsell_stats?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="upsell-conversion-stats">
          {data.upsell_stats.map((u) => (
            <span key={u.milestone} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs">
              {u.milestone.replace(/_/g, " ")}: {u.sent} sent → {u.converted} Pro ({u.conversion_pct}%)
            </span>
          ))}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Replace each URL with your real referral/affiliate link. Toggle off to hide instantly — no deploy needed.</p>
      <div className="mt-4 space-y-2">
        {data.partners.map((p) => (
          <div key={p.partner_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`partner-admin-${p.partner_id}`}>
            <span className="min-w-[140px] font-medium">{p.name}</span>
            <input defaultValue={p.url} onBlur={(e) => saveUrl(p, e.target.value.trim())}
              className="h-8 min-w-[200px] flex-1 rounded-md border border-border bg-card px-2 text-xs" data-testid={`partner-url-${p.partner_id}`} />
            <span className="text-xs text-muted-foreground">{p.clicks_7d} clicks 7d · {p.clicks_total} total</span>
            <button onClick={() => toggle(p)} data-testid={`partner-toggle-${p.partner_id}`}
              className={`rounded-full px-2.5 py-1 text-xs ${p.active ? "bg-emerald-500/10 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
              {p.active ? "Active" : "Off"}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <input placeholder="Partner name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="partner-new-name" />
        <input placeholder="Referral URL (https://...)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="partner-new-url" />
        <input placeholder="Offer text" value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm sm:col-span-2" data-testid="partner-new-offer" />
      </div>
      <Button size="sm" className="mt-2" onClick={add} data-testid="partner-add-btn">Add partner</Button>
    </div>
  );
}

function ReconciliationPanel() {
  const [rec, setRec] = useState(null);
  useEffect(() => {
    api.get("/admin/reconciliation").then((r) => setRec(r.data)).catch(() => {});
  }, []);
  if (!rec) return null;
  const C = rec.canonical;
  return (
    <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-card p-6" data-testid="reconciliation-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg">One Ledger — Reconciliation</h3>
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${rec.reconciled ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="reconciliation-status">
          {rec.reconciled ? "✓ ALL LIVE SOURCES RECONCILED" : "⚠ LIVE SOURCES DIVERGED"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="reconciliation-metrics">
        <Stat label="Verified REAL revenue" value={`£${C.verified_real}`} />
        <Stat label="Test data excluded" value={`£${C.test_excluded}`} />
        <Stat label="Admin excluded" value={`£${C.admin_excluded}`} />
        <Stat label="Net real (after refunds)" value={`£${C.net_real}`} />
        <Stat label="Real payments" value={C.payments_confirmed_real} />
      </div>
      <div className="mt-3 space-y-1.5" data-testid="reconciliation-checks">
        {rec.checks.map((c, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-1 rounded-lg border border-border bg-background px-3 py-2 text-xs">
            <span className={c.authoritative ? "font-semibold text-emerald-400" : "text-muted-foreground"}>{c.source}</span>
            <span className="font-mono">£{c.value}</span>
          </div>
        ))}
      </div>
      {rec.real_transactions?.length > 0 && (
        <div className="mt-3" data-testid="reconciliation-drilldown">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Every real penny, traced</p>
          <div className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-border">
            {rec.real_transactions.map((t, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-1 border-b border-border px-3 py-1.5 text-[11px] last:border-0">
                <span className="font-mono text-muted-foreground">{t.id}</span>
                <span>{t.product}</span>
                <span className="truncate max-w-[140px] text-muted-foreground">{t.email}</span>
                <span className="font-mono">£{t.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {rec.real_transactions?.length === 0 && (
        <p className="mt-3 text-xs text-amber-400" data-testid="reconciliation-empty-note">
          No verified real-customer payments yet. All historical revenue records came from test/QA accounts — preserved untouched, excluded from truth metrics.
        </p>
      )}
      <p className="mt-2 text-[10px] text-muted-foreground" data-testid="reconciliation-freshness">
        Computed {C.freshness?.computed_at?.slice(0, 19).replace("T", " ")} UTC · Last payment record: {C.freshness?.last_payment_at?.slice(0, 10) || "—"} · Classification is read-time; no historical records modified.
      </p>
    </div>
  );
}

function ControlPlanePanel() {
  const [cp, setCp] = useState(null);
  useEffect(() => {
    api.get("/admin/control-plane").then(({ data }) => setCp(data)).catch(() => {});
  }, []);
  if (!cp) return null;
  const E = cp.events, R = cp.revenue, U = cp.users, T = cp.target_199;
  const EventRow = ({ label, w }) => (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{w.total}</p>
      <p className="text-[11px] text-muted-foreground">today {w.today} · 7d {w["7d"]} · 30d {w["30d"]}</p>
    </div>
  );
  return (
    <div className="mb-6 rounded-xl border border-accent/40 bg-card p-6" data-testid="control-plane-panel">
      <h3 className="font-display text-lg">Founder Control Plane — Product & Revenue Events</h3>
      {cp.generated_at && (
        <p className="mt-0.5 text-[10px] text-muted-foreground" data-testid="data-freshness">
          Data updated: {new Date(cp.generated_at).toLocaleTimeString()} · computed live from the canonical ledger
        </p>
      )}
      <p className="text-xs text-muted-foreground">All figures from authoritative backend records. Verified payments only — never checkout initiations.</p>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <EventRow label="Goals created" w={E.goals_created} />
        <EventRow label="Tasks completed (tracked)" w={E.tasks_completed} />
        <EventRow label="Verified outcomes" w={E.verified_outcomes} />
        <EventRow label={`Payments confirmed (real: ${E.payments_confirmed_real ?? 0} · test: ${E.payments_confirmed_test ?? 0})`} w={E.payments_confirmed} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Verified revenue (all)" value={`£${R.verified_total}`} />
        <Stat label="Verified revenue 30d" value={`£${R.verified_30d}`} />
        <Stat label="MRR" value={`£${R.mrr}`} />
        <Stat label="Paid users (verified)" value={`${U.paid} / ${U.total}`} />
        <Stat label="Free → Paid" value={`${U.conversion_pct}%`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="refunds-row">
        <Stat label="Refunds (money out)" value={`−£${R.refunds_total ?? 0}`} />
        <Stat label="Refunds 30d" value={`−£${R.refunds_30d ?? 0}`} />
        <Stat label="Chargebacks" value={R.chargebacks_count ?? 0} />
        <Stat label="Net revenue" value={`£${R.net_total ?? R.verified_total}`} />
      </div>
      {cp.affiliate && (
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-7" data-testid="affiliate-metrics-row">
          <Stat label="Affiliates" value={cp.affiliate.affiliates} />
          <Stat label="Aff. clicks" value={cp.affiliate.clicks} />
          <Stat label="Aff. signups" value={cp.affiliate.signups} />
          <Stat label="Conversions" value={cp.affiliate.conversions} />
          <Stat label="Commission pending" value={`£${cp.affiliate.pending}`} />
          <Stat label="Commission approved" value={`£${cp.affiliate.approved}`} />
          <Stat label="Commission paid" value={`£${cp.affiliate.paid}`} />
        </div>
      )}
      {cp.dispatch && (
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-6" data-testid="dispatch-metrics-row">
          <Stat label="Dispatch active" value={cp.dispatch.active} />
          <Stat label="Dispatch completed" value={cp.dispatch.completed} />
          <Stat label="Dispatch failed" value={cp.dispatch.failed} />
          <Stat label="Disputed" value={cp.dispatch.disputed} />
          <Stat label="Providers" value={cp.dispatch.providers} />
          <Stat label="Remittance clicks" value={cp.remittance_clicks ?? 0} />
        </div>
      )}
      {U.access_granted_nonpaying > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="access-granted-note">
          {U.access_granted_nonpaying} additional account(s) have Pro ACCESS without payment (test/QA/granted) — excluded from paid metrics per Zero False Success.
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="revenue-by-engine">
        {Object.entries(R.by_engine).map(([eng, amt]) => (
          <span key={eng} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs">
            {eng}: £{amt}
          </span>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="target-199">
        <p className="font-medium">£{T.plan_price}/mo plan — target scenario (not a forecast)</p>
        <p className="mt-1 text-xs text-muted-foreground">
          10% of {U.total} users = {T.scenario_10pct_subscribers} subscribers → £{T.scenario_10pct_mrr}/mo ·
          Current MRR £{T.current_mrr} · Gap £{T.gap_to_scenario} · {T.subscribers_required} × £{T.plan_price} subscribers needed
        </p>
      </div>
      {cp.funnel && (
        <div className="mt-5" data-testid="conversion-funnel">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Conversion funnel — where users drop</p>
          <div className="mt-3 space-y-1.5">
            {cp.funnel.map((s, i) => {
              const max = cp.funnel[0].users || 1;
              const prev = i > 0 ? cp.funnel[i - 1].users : s.users;
              const dropPct = prev ? Math.round((1 - s.users / prev) * 100) : 0;
              return (
                <div key={s.stage} className="flex items-center gap-2 text-xs">
                  <span className="w-56 shrink-0 text-muted-foreground">{s.stage}</span>
                  <div className="h-5 flex-1 rounded bg-background">
                    <div className="flex h-5 items-center rounded bg-primary/70 px-1.5 text-[10px] font-semibold text-white"
                      style={{ width: `${Math.max((s.users / max) * 100, 4)}%` }}>
                      {s.users}
                    </div>
                  </div>
                  <span className={`w-20 shrink-0 text-right ${i > 0 && dropPct >= 50 ? "text-red-400" : "text-muted-foreground"}`}>
                    {i > 0 ? `-${dropPct}%` : "100%"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
